import React from 'react';
import { Star, Lock, Edit2, Archive } from 'lucide-react';
import { Badge, Button, Card, ProgressBar } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatPoints } from '@/shared/utils/format';
import type { Reward } from '@/entities/reward';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface RewardCardProps {
  reward: Reward;
  availablePoints: number;
  onRedeem: () => void;
  onEdit?: () => void;
  onArchive?: () => void;
  userRole: 'parent' | 'child';
}

// ---------------------------------------------------------------------------
// RewardCard component
// ---------------------------------------------------------------------------
export default function RewardCard({
  reward,
  availablePoints,
  onRedeem,
  onEdit,
  onArchive,
  userRole,
}: RewardCardProps): React.ReactElement {
  const isAffordable = availablePoints >= reward.pointCost;
  const progressPercent = Math.min(100, Math.round((availablePoints / reward.pointCost) * 100));
  const pointsNeeded = Math.max(0, reward.pointCost - availablePoints);

  // ---- Parent view ----
  if (userRole === 'parent') {
    return (
      <Card className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {reward.emoji && (
              <span className="text-2xl leading-none" aria-hidden="true">
                {reward.emoji}
              </span>
            )}
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-slate-900">{reward.name}</h3>
              {reward.description && (
                <p className="line-clamp-1 text-xs text-slate-500 mt-0.5">{reward.description}</p>
              )}
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                aria-label={`Edit ${reward.name}`}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg text-slate-500',
                  'hover:bg-slate-100 hover:text-slate-700',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  'transition-colors duration-150',
                )}
              >
                <Edit2 size={15} aria-hidden="true" />
              </button>
            )}
            {onArchive && (
              <button
                type="button"
                onClick={onArchive}
                aria-label={`Archive ${reward.name}`}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg text-slate-500',
                  'hover:bg-slate-100 hover:text-slate-700',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  'transition-colors duration-150',
                )}
              >
                <Archive size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="info">
            <Star size={11} aria-hidden="true" />
            {formatPoints(reward.pointCost)}
          </Badge>
          {reward.category && <Badge variant="neutral">{reward.category}</Badge>}
        </div>
      </Card>
    );
  }

  // ---- Child view ----
  return (
    <Card
      className={cn(
        'flex flex-col gap-4 transition-all duration-200',
        !isAffordable && 'opacity-80',
        isAffordable ? 'border-secondary-300 shadow-card-md' : 'border-slate-200',
      )}
      aria-label={`${reward.name}, costs ${reward.pointCost} points${isAffordable ? ', affordable' : `, need ${pointsNeeded} more points`}`}
    >
      {/* Emoji / icon */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-2xl text-3xl',
            isAffordable ? 'bg-secondary-100' : 'bg-slate-100',
          )}
          aria-hidden="true"
        >
          {reward.emoji ?? '🎁'}
        </div>
        <h3 className="text-base font-bold text-slate-900 leading-tight">{reward.name}</h3>
        {reward.description && (
          <p className="text-xs text-slate-500 leading-relaxed">{reward.description}</p>
        )}
      </div>

      {/* Point cost */}
      <div className="flex items-center justify-center gap-1.5">
        <Star size={16} className="text-accent-500" aria-hidden="true" />
        <span className="text-lg font-bold text-slate-900">{reward.pointCost.toLocaleString()}</span>
        <span className="text-sm text-slate-500">pts</span>
      </div>

      {/* Progress bar */}
      <ProgressBar
        value={progressPercent}
        colorScheme={isAffordable ? 'secondary' : 'primary'}
        label={isAffordable ? 'You can afford this!' : `${progressPercent}% of the way there`}
        showPercentage={!isAffordable}
      />

      {/* Points needed label */}
      {!isAffordable && (
        <p className="text-center text-xs text-slate-500" aria-live="polite">
          You need{' '}
          <span className="font-semibold text-slate-700">{pointsNeeded.toLocaleString()} more pts</span>
        </p>
      )}

      {/* Redeem button */}
      <Button
        variant="primary"
        size="lg"
        fullWidth
        disabled={!isAffordable}
        onClick={onRedeem}
        className={cn(
          'min-h-[48px]',
          isAffordable
            ? 'bg-secondary-600 hover:bg-secondary-700 focus-visible:ring-secondary-500'
            : '',
        )}
        leftIcon={!isAffordable ? <Lock size={16} aria-hidden="true" /> : undefined}
        aria-label={
          isAffordable
            ? `Redeem ${reward.name} for ${reward.pointCost} points`
            : `${reward.name} locked — need ${pointsNeeded} more points`
        }
      >
        {isAffordable ? 'Redeem' : 'Locked'}
      </Button>
    </Card>
  );
}
